import { KeyboardEvent, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDropDown as ArrowDropDownIcon, InfoTwoTone as InfoIcon } from '@mui/icons-material';
import { styled, Tooltip } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, isObject } from 'lodash';
import Select, {
  components,
  CSSObjectWithLabel,
  FilterOptionOption,
  MultiValue,
  OptionProps,
  SingleValue,
  StylesConfig,
} from 'react-select';
import { useZoom } from 'context/ZoomContext';
import theme from 'theme';
import { toString } from 'utilities';
import {
  CustomSelectProps,
  DropdownIndicatorPropsBase,
  IsMulti,
  MatchFrom,
  OptionPropsBase,
  OptionType,
  StateEvent,
} from './types';

const PREFIX = 'CustomSelect';

const classes = {
  dropdownIndicator: `${PREFIX}-dropdownIndicator`,
  tooltip: `${PREFIX}-tooltip`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.dropdownIndicator}`]: {
    marginLeft: '0.26em',
    padding: '0.16em !important',
  },
}));

const StyledGridWithConditional = styled(Grid)<{ isSelected: boolean }>(({ isSelected }) => ({
  color: isSelected ? theme.palette.common.white : theme.palette.primary[700],
}));

// JavaScript key codes
const TAB_KEY = 'Tab';
const ENTER_KEY = 'Enter';
const ESCAPE_KEY = 'Escape';

const ITEM_HEIGHT = 28;

const containerStyle = (styles: CSSObjectWithLabel) => ({
  ...styles,
  fontWeight: 400,
  height: ITEM_HEIGHT,
  marginTop: '2px',
  padding: '2px 2px 4px',
});

const selectStyles: StylesConfig<OptionType, IsMulti> = {
  control: styles => ({
    ...styles,
    backgroundColor: theme.palette.common.white,
    border: 'none',
    borderColor: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    height: 'auto',
    left: 0,
    minHeight: ITEM_HEIGHT,
    position: 'absolute',
    top: 0,
    width: '100%',
  }),
  container: () => ({
    // This is intentional just to remove the default styles on container
  }),
  valueContainer: styles => ({
    ...containerStyle(styles),
    height: 'auto', // Allow height to grow with content
    minHeight: ITEM_HEIGHT - 8, // Subtract padding
  }),
  singleValue: styles => ({
    ...styles,
    color: theme.palette.primary[700],
    lineHeight: 1.2, // Better line spacing for wrapped text
    marginRight: 0,
    right: 0,
    top: '50%',
    whiteSpace: 'normal', // Allow text wrapping
    wordWrap: 'break-word', // Break long words if needed
  }),
  indicatorSeparator: styles => ({
    ...styles,
    display: 'none',
  }),
  indicatorsContainer: styles => ({
    ...containerStyle(styles),
    alignSelf: 'flex-start', // Align to top when content wraps
    padding: 0,
  }),
  option: (styles, state) => ({
    ...styles,
    color: state.isSelected ? theme.palette.common.white : theme.palette.primary[700],
    fontSize: '14px',
    lineHeight: 1.2, // Better line spacing for wrapped text
    minHeight: '20px', // Ensure minimum height for readability
    overflow: 'visible',
    padding: '7.5px 12px',
    whiteSpace: 'normal', // Allow wrapping
    wordWrap: 'break-word', // Break long words if needed
  }),
  menuList: styles => ({
    ...styles,
    backgroundColor: theme.palette.common.white,
    bottom: '100%',
    display: 'block',
    maxWidth: '100%',
    overflowX: 'hidden',
    paddingBottom: 0,
    paddingTop: 0,
    whiteSpace: 'normal',
  }),
  menu: (styles, state) => {
    const { placement } = state;
    const isTop = placement === 'top';

    return {
      ...styles,
      borderRadius: 0,
      fontWeight: 400,
      left: 0,
      marginBottom: isTop ? `-${theme.palette.cellHeight}` : 0,
      marginTop: isTop ? 0 : `-${theme.palette.cellHeight}`,
      zIndex: 99,
    };
  },
  menuPortal: styles => ({
    ...styles,
    height: 'auto', // Changed from fixed height to auto
    margin: '-2rem 0 0',
    minHeight: ITEM_HEIGHT,
    zIndex: 9999,
  }),
};

const CustomTooltip = Tooltip;

const StyledGridWithConditionalTooltip = ({
  children,
  isSelected = false,
  label,
  shouldDisplayTooltip,
}: {
  children: ReactNode;
  isSelected: boolean;
  label: string;
  shouldDisplayTooltip: boolean;
}) =>
  shouldDisplayTooltip ? (
    <CustomTooltip placement="bottom-start" title={label}>
      <StyledGridWithConditional as="span" isSelected={isSelected}>
        {children}
      </StyledGridWithConditional>
    </CustomTooltip>
  ) : (
    <StyledGridWithConditional as="span" isSelected={isSelected}>
      {children}
    </StyledGridWithConditional>
  );

const Option = (props: OptionPropsBase) => {
  const { children, containerWidth, data, ...rest } = props;
  const { label = '', tooltip = '', isTitle = false } = data;

  const [optionWidth, setOptionWidth] = useState(0);

  const optionRef = useRef<HTMLDivElement | null>(null);

  const { zoomLevel } = useZoom();

  const shouldDisplayTooltip = useMemo(() => optionWidth > containerWidth, [optionWidth, containerWidth]);

  useLayoutEffect(() => {
    if (optionRef?.current) {
      setOptionWidth(optionRef.current.offsetWidth);
    }
  }, [optionRef]);

  // For title options, render a non-interactive element
  if (isTitle) {
    return (
      <div
        style={{
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.secondary,
          cursor: 'default',
          fontSize: `${14 * zoomLevel}px`,
          fontWeight: 700,
          padding: '8px 12px',
        }}
        ref={optionRef}>
        {!isEmpty(tooltip) ? (
          <div style={{ position: 'relative', width: '100%' }}>
            <StyledGridWithConditionalTooltip
              isSelected={rest.isSelected}
              label={label}
              shouldDisplayTooltip={shouldDisplayTooltip}>
              {children}
            </StyledGridWithConditionalTooltip>
            <div
              style={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
              }}>
              <Tooltip placement="top-end" title={tooltip} arrow>
                <InfoIcon />
              </Tooltip>
            </div>
          </div>
        ) : (
          <StyledGridWithConditionalTooltip
            isSelected={rest.isSelected}
            label={label}
            shouldDisplayTooltip={shouldDisplayTooltip}>
            {children}
          </StyledGridWithConditionalTooltip>
        )}
      </div>
    );
  }

  return (
    <components.Option {...{ ...rest, data }}>
      {!isEmpty(tooltip) ? (
        <div style={{ position: 'relative', width: '100%' }}>
          <StyledGrid ref={optionRef}>
            <StyledGridWithConditionalTooltip
              isSelected={rest.isSelected}
              label={label}
              shouldDisplayTooltip={shouldDisplayTooltip}>
              {children}
            </StyledGridWithConditionalTooltip>
          </StyledGrid>
          <div
            style={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
            }}>
            <Tooltip placement="top-end" title={tooltip} arrow>
              <InfoIcon />
            </Tooltip>
          </div>
        </div>
      ) : (
        <StyledGrid ref={optionRef}>
          <StyledGridWithConditionalTooltip
            isSelected={rest.isSelected}
            label={label}
            shouldDisplayTooltip={shouldDisplayTooltip}>
            {children}
          </StyledGridWithConditionalTooltip>
        </StyledGrid>
      )}
    </components.Option>
  );
};

const DropdownIndicator = (props: DropdownIndicatorPropsBase) => (
  <components.DropdownIndicator {...props} className={classes.dropdownIndicator}>
    <ArrowDropDownIcon />
  </components.DropdownIndicator>
);

const filterByLabelOnly = (option: FilterOptionOption<OptionType>, search: string, matchFrom: MatchFrom): boolean => {
  const input = toString(search).trim().toLowerCase();

  if (!input) {
    return true;
  }

  const label = option.label?.toString().trim().toLowerCase() ?? '';

  if (matchFrom === 'start') {
    return label.startsWith(input);
  }

  return label.includes(input);
};

const CustomSelect = ({
  autoFocus = true,
  disabled,
  enumerated = false,
  extraStyles,
  isMulti = false,
  matchFrom = 'start',
  menuPlacement,
  menuPosition = 'absolute',
  onChange,
  onCommit,
  onEmptyValue,
  onRevert,
  openMenuOnFocus = true,
  options,
  setLabelAsValue,
  usePortal,
  value,
}: CustomSelectProps) => {
  const [selectedValue, setSelectedValue] = useState<OptionType | OptionType[]>();
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [state, setState] = useState<StateEvent>({ e: null });

  const containerRef = useRef<HTMLDivElement | null>(null);

  const optionList = useMemo(() => {
    const tmpList: OptionType[] = [];

    if (options) {
      options.forEach((option, index) => {
        let parsedOption = option;

        if (!isObject(option)) {
          parsedOption = {
            value: toString(index),
            label: toString(option),
          };
        }

        if (parsedOption) {
          tmpList.push(parsedOption);
        }
      });
    }

    return tmpList;
  }, [options]);

  const handleChange = (newValue: SingleValue<OptionType> | MultiValue<OptionType>) => {
    if (isMulti) {
      const list = (newValue as MultiValue<OptionType>) || [];
      const selectedValues = list.map(opt => (enumerated ? opt?.label : toString(opt?.value)));
      const { e } = state;
      onCommit?.(selectedValues, e);
      onChange?.(selectedValues);
      return;
    }

    const opt = newValue as OptionType;
    const selected = enumerated ? opt?.label : toString(opt?.value);

    if (!opt && onRevert) {
      onRevert();
      return;
    }

    const { e } = state;
    onCommit?.(selected, e);

    onChange?.(selected);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Abort edition
    if (e.key === ESCAPE_KEY && onRevert) {
      onRevert();
    }

    if ([ENTER_KEY, TAB_KEY].includes(e.key)) {
      e.persist();
      setState({ e });
    } else {
      setState({ e: null });
    }
  };

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [containerRef, selectedValue]);

  useEffect(() => {
    /*
     * These validations are intended for the Select component when we use it directly
     * bypassing the SelectValueViewer. In these cases, we need to set the option label
     * manually here (currently, only in the Fund Ownership and Allocation pages).
     */
    if (isMulti && Array.isArray(value)) {
      const valuesArray = value as (string | number)[];
      const selectedOptions = valuesArray
        .map(v => optionList.find(opt => toString(opt.value) === toString(v)))
        .filter(Boolean) as OptionType[];
      setSelectedValue(selectedOptions);
      return;
    }

    let itemLabel = '';
    let itemValue = null;
    // Resolve selected option by value first, fallback to label
    const itemSelected =
      optionList.find(opt => toString(opt.value) === toString(value)) ??
      optionList.find(opt => toString(opt.label) === toString(value));

    // Fund Ownership: In the Shares Ledger modal, for the date field used in the Purchased section.
    if (setLabelAsValue) {
      itemLabel = (value as string) ?? onEmptyValue;
      itemValue = (value as string) ?? null;
    } else {
      itemLabel = itemSelected?.label ?? onEmptyValue;
      itemValue = itemSelected?.value ?? itemSelected?.id ?? null;
    }
    setSelectedValue({ value: itemValue, label: itemLabel });
  }, [value, setLabelAsValue, optionList, onEmptyValue, isMulti]);

  return (
    <Grid ref={containerRef} width="100%">
      <Select
        autoFocus={autoFocus}
        closeMenuOnSelect={!isMulti}
        components={{
          Option: (props: OptionProps<OptionType, IsMulti>) => Option({ ...props, containerWidth }),
          DropdownIndicator,
        }}
        filterOption={(option, search) => filterByLabelOnly(option, search, matchFrom)}
        instanceId="scalar"
        isDisabled={disabled || isEmpty(optionList)}
        isMulti={isMulti}
        isOptionDisabled={option => !!option.isTitle}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        openMenuOnClick
        openMenuOnFocus={openMenuOnFocus}
        options={optionList}
        placeholder={onEmptyValue}
        styles={{ ...selectStyles, ...extraStyles }}
        value={selectedValue}
        {...(usePortal && { menuPortalTarget: document.body })}
      />
    </Grid>
  );
};

export default CustomSelect;
